import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Copy,
  ArrowRight,
  Info,
  ShieldCheck,
  Lightbulb,
  Mail,
} from "lucide-react";
import { runSnowballAnalysis, type SnowballAnalysis, type PolicyLoanEntry } from "@/lib/snowballEngine";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import type { PaymentFrequency } from "@/lib/calculationEngine";
import { toast } from "sonner";

interface MultiPolicyInputEntry {
  amount: number;
  startDate: string;
  policyNumber: string;
  useSameStartDate?: boolean;
  useSameAnniversary?: boolean;
  anniversaryMonth?: number;
  anniversaryDay?: number;
}

interface MultiPolicySnowballProps {
  isMultiPolicy: boolean;
  policyCount: number;
  policyInputs: MultiPolicyInputEntry[];
  totalLoanAmount: number;
  paymentAmount: number;
  paymentFrequency: PaymentFrequency;
  firstPaymentDate: string;
  policyLoanRate: number; // decimal
  jurisdiction: Jurisdiction;
}

const frequencyLabels: Record<PaymentFrequency, string> = {
  weekly: 'weekly',
  biweekly: 'bi-weekly',
  semimonthly: 'semi-monthly',
  monthly: 'monthly',
  quarterly: 'quarterly',
  semiannual: 'semi-annual',
  annual: 'annual',
  endofterm: 'end of term',
};

export function MultiPolicySnowball({
  isMultiPolicy,
  policyCount,
  policyInputs,
  totalLoanAmount,
  paymentAmount,
  paymentFrequency,
  firstPaymentDate,
  policyLoanRate,
  jurisdiction,
}: MultiPolicySnowballProps) {
  const { fmt } = createFormatter(jurisdiction);
  const [isOpen, setIsOpen] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Validation
  // Policy 1 amount = total minus others
  const othersTotal = policyInputs.slice(1, policyCount).reduce((s, p) => s + (p.amount || 0), 0);
  const policy1Amount = Math.max(0, totalLoanAmount - othersTotal);
  const resolvedPolicies = policyInputs.slice(0, policyCount).map((p, i) => ({
    ...p,
    amount: i === 0 ? policy1Amount : p.amount,
  }));
  const activePolicies = resolvedPolicies.filter(p => p.amount > 0);
  const totalPolicyAmounts = activePolicies.reduce((sum, p) => sum + p.amount, 0);
  const hasError = othersTotal > totalLoanAmount + 0.01;
  const hasWarning = false; // No longer needed since Policy 1 auto-adjusts
  const isValid = activePolicies.length >= 2 && !hasError && paymentAmount > 0;

  // Run snowball analysis
  const analysis = useMemo<SnowballAnalysis | null>(() => {
    if (!isValid) return null;

    const entries: PolicyLoanEntry[] = activePolicies.map((p, i) => ({
      id: `policy-${i + 1}`,
      label: `Policy ${i + 1}`,
      amount: p.amount,
      startDate: (p.useSameStartDate !== false ? '' : p.startDate) || firstPaymentDate,
      policyNumber: p.policyNumber,
      interestRate: policyLoanRate,
    }));

    return runSnowballAnalysis({
      policies: entries,
      paymentAmount,
      paymentFrequency,
      firstPaymentDate,
      policyLoanRate,
    });
  }, [activePolicies, paymentAmount, paymentFrequency, firstPaymentDate, policyLoanRate, isValid]);

  if (!isMultiPolicy) return null;

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none hover:bg-muted/30 transition-colors pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Multi-Policy Loan Snowball Strategy
              <Badge variant="secondary" className="text-[10px] ml-1">Advanced</Badge>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-5">

            {/* Validation Messages */}
            {hasError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Amounts Exceed Total Loan</AlertTitle>
                <AlertDescription className="text-xs">
                  The sum of individual policy loan amounts ({fmt(totalPolicyAmounts)}) exceeds the total loan amount ({fmt(totalLoanAmount)}). Please correct the amounts before proceeding.
                </AlertDescription>
              </Alert>
            )}

            {hasWarning && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Total Mismatch</AlertTitle>
                <AlertDescription className="text-xs">
                  Total policy loan amounts ({fmt(totalPolicyAmounts)}) are less than the total loan entered ({fmt(totalLoanAmount)}). Confirm accuracy.
                </AlertDescription>
              </Alert>
            )}

            {activePolicies.length < 2 && (
              <div className="text-center py-4 text-sm text-muted-foreground">
                Enter amounts for at least 2 policies to generate the snowball strategy.
              </div>
            )}

            {/* Results */}
            {analysis && (
              <>
                {/* Optimal Order */}
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-sm">Optimal Policy Paydown Order</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {analysis.optimal.phases.map((phase, i) => (
                      <span key={phase.policyId} className="flex items-center gap-1.5">
                        {i > 0 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
                        <Badge variant="outline" className="text-xs font-semibold px-2.5 py-1">
                          {phase.policyLabel}
                          {phase.policyNumber && <span className="text-muted-foreground ml-1">#{phase.policyNumber}</span>}
                        </Badge>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This order minimizes total interest paid to the life company.
                  </p>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Est. Interest</p>
                      <p className="text-lg font-bold tabular-nums">{fmt(analysis.optimal.totalInterest)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Payments</p>
                      <p className="text-lg font-bold tabular-nums">{analysis.optimal.totalPayments}</p>
                    </div>
                  </div>
                </div>

                {/* Phase Breakdown */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Repayment Phases</h3>
                  {analysis.optimal.phases.map((phase, i) => (
                    <div
                      key={phase.policyId}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <p className="text-sm font-medium">
                          {phase.policyLabel}
                          {phase.policyNumber && (
                            <span className="text-muted-foreground"> — #{phase.policyNumber}</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Paid in <span className="font-semibold text-foreground">{phase.paymentsInPhase} payments</span>
                          {' · '}Starting balance: {fmt(phase.startingBalance)}
                          {' · '}Est. interest across all policies during phase: {fmt(phase.interestInPhase)}
                        </p>
                      </div>
                    </div>
                   ))}
                   <p className="text-[10px] text-muted-foreground italic pl-10">
                     Note: Interest continues to accrue on all outstanding policies during each phase, not just the one receiving payments. The interest shown per phase includes accrued interest across all policies.
                   </p>
                 </div>

                {/* Implementation Instructions */}
                <div className="rounded-lg border-2 border-amber-300/50 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-700/30 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <h3 className="font-semibold text-sm">Implementation Instructions</h3>
                  </div>
                   <p className="text-xs text-muted-foreground leading-relaxed">
                     This strategy requires active management by the policy owner.
                     Payments must be directed intentionally to specific policies.
                     The life insurance company will <span className="font-semibold text-foreground">NOT</span> automatically
                     apply payments across multiple policies in this optimized way.
                   </p>

                  <div className="space-y-3 pt-2 border-t border-amber-200/50 dark:border-amber-800/30">
                    {analysis.optimal.phases.map((phase, i) => {
                      const freqLabel = frequencyLabels[paymentFrequency];
                      const letterText = `Please apply my ${freqLabel} payment of ${fmt(paymentAmount)} to Policy #${phase.policyNumber || '____'} and apply it toward the outstanding loan balance for the next ${phase.paymentsInPhase} payments.`;

                      return (
                        <div key={phase.policyId} className="space-y-1.5">
                          <p className="text-xs font-semibold">
                            Phase {i + 1}: {phase.policyLabel}
                            {phase.policyNumber && ` (#${phase.policyNumber})`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Direct your full payment of <span className="font-semibold text-foreground">{fmt(paymentAmount)}</span> to{' '}
                            {phase.policyLabel}{phase.policyNumber ? ` (#${phase.policyNumber})` : ''} each period
                            until it is paid off ({phase.paymentsInPhase} payments).
                            {i < analysis.optimal.phases.length - 1 && (
                              <> Then redirect the full payment to {analysis.optimal.phases[i + 1].policyLabel}.</>
                            )}
                          </p>

                          {/* Copyable letter */}
                          <div className="relative group">
                            <div className="rounded border border-border bg-background p-2.5 text-xs text-muted-foreground pr-9 font-mono leading-relaxed">
                              {letterText}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="absolute top-1 right-1 h-7 w-7 p-0 opacity-60 hover:opacity-100"
                              onClick={() => copyToClipboard(letterText, i)}
                            >
                              {copiedIndex === i ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Helpful Tip Box */}
                <div className="rounded-lg border border-green-300/50 bg-green-50/60 dark:bg-green-950/20 dark:border-green-700/30 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <h3 className="font-semibold text-sm">Helpful Tip</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    It is always recommended that any email communication to the Life Insurance Company should include the <span className="font-semibold text-foreground">policy number as the first thing in your subject line</span> and a short description. This also creates a helpful record for your inbox so when you search that policy you can pull a clear and easy to understand history of policy loan or other communication in connection with that specific contract.
                  </p>
                  <div className="rounded border border-border bg-background p-2.5 text-xs text-muted-foreground font-mono">
                    <span className="flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      EG) #3000111ABC - Monthly Loan Repayments
                    </span>
                  </div>
                </div>

                {/* Communication Guidance */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-xs font-semibold">Communication Guidance</AlertTitle>
                  <AlertDescription className="text-xs text-muted-foreground leading-relaxed">
                    You may need to clearly communicate to your life insurance company how payments should be applied.
                    Use the copyable templates above to instruct them for each phase.
                  </AlertDescription>
                </Alert>

                {/* Behavioral Emphasis */}
                <p className="text-[10px] text-muted-foreground leading-relaxed border-l-2 border-primary/30 pl-3">
                  This strategy only works if payments are consistently directed and followed as planned.
                  Discipline and communication are essential. Each payment must go to the correct policy
                  in the designated order. Deviating from the plan may result in higher total interest costs.
                </p>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
